import * as i from 'types';
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next';
import { editMe } from 'ducks/me';
import {
  generatePaymentUrl, isBrowser, validation, triggerGtagEvent,
  memberShipOptions, classOptions,
} from 'services';
import { loadStripe } from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'services/hooks';
import { editMeLoading, meDataSelector } from 'selectors/me';
import { orderDataSelector, orderPaymentInfoSelector } from 'selectors/orders';
import { useCheckoutContext } from 'modules/PricingPage';
import { StepButtonGroup } from 'modules/StepButtonGroup';
import { Input, InputRadio } from 'common/form';
import { Button } from 'common/interaction';
import { Text } from 'common/typography';
import { getAuthenticationToken } from 'services/authentication/api';

import {
  AddressContainer, StyledForm, CheckoutFooter, ExtendedText,
  PaymentContainer, FormContainer,
} from './styled';

export const CheckoutForm = () => {
  const { language } = useI18next();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(editMeLoading);
  const orderData = useSelector(orderDataSelector);
  const userData = useSelector(meDataSelector);
  const paymentInfo = useSelector(orderPaymentInfoSelector);
  const { currentStep, activeItem } = useCheckoutContext();
  const [error, setError] = React.useState<string | null>(null);
  const [isPaymentLoading, setIsPaymentLoading] = React.useState(false);
  const smallDescription = activeItem?.description?.substring(
    activeItem?.description?.indexOf('[') + 1,
    activeItem?.description?.lastIndexOf(']'),
  );

  const defaultValues = {
    address: userData?.address || '',
    zip: userData?.zip || '',
    city: userData?.city || '',
    phone: userData?.phone || '',
    payment_method: activeItem?.unlimited ? 'sepa' : 'ideal',
    iban: '',
  };

  const createPaymentIntent = async (method: 'ideal' | 'sepa_debit', user) => {
    const stripe = await loadStripe(paymentInfo?.gateway.stripeKey);
    const token = await getAuthenticationToken();

    const methodPayload = {
      type: method,
      billing_details: {
        name: `${user?.firstName} ${user?.lastName}`,
        email: user?.username,
      },
    };

    if (method === 'sepa_debit') {
      //@ts-ignore
      methodPayload.sepa_debit = { iban: user.iban };
    }

    if (method === 'ideal') {
      //@ts-ignore
      methodPayload.ideal = {};
    }

    //@ts-ignore
    const { paymentMethod } = await stripe?.createPaymentMethod(methodPayload);

    if (!paymentMethod) throw new Error('No payment method');

    const methodResponse = await fetch(
      `https://api.zingfit.com/payments/${orderData?.id}/paymentmethodTypes/${paymentMethod?.id}`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
          'X-ZINGFIT-REGION-ID': '1531268836941104412',
        },
        body: JSON.stringify({
          email: userData?.username,
          currency: 'eur',
          amount: orderData?.total.amount,
          paymentMethodType: method,
          return_url: `${window.location.origin}/${language}/paymentsuccess?orderId=${orderData?.id}&paymentMethod=${method}`,
        }),
      },
    ).then((res) => res.json());

    if (!methodResponse) throw new Error('No payment method response');

    return methodResponse;
  };

  const sepaPayment = async (clientSecret: string) => {
    const token = await getAuthenticationToken();
    const intent = clientSecret.split('_secret_')[0];

    if (!intent) throw new Error('No payment intent');

    const result = await fetch(
      `https://api.zingfit.com/payments/${orderData?.id}/sepadebit`,
      {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
          'X-ZINGFIT-REGION-ID': '1531268836941104412',
        },
        body: JSON.stringify({
          sourceId: intent,
        }),
      },
    ).then((res) => res.json());

    if (!result) throw console.error('No result');

    return result;
  };

  const {
    register, control, handleSubmit, watch, formState: { isValid, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });
  const handleCheckout = (data: MembershipForm) => {
    if (!userData || isPaymentLoading) return;
    setIsPaymentLoading(true);

    const user: i.User = {
      ...userData,
      ...data,
    };
    dispatch(editMe(user))
      .then(() => {
        if (!orderData || !paymentInfo) return;

        if (data.payment_method === 'ideal') {
          createPaymentIntent('ideal', user)
            .then((paymentIntent) => {
              if (!paymentIntent) return;
              if (paymentIntent?.url) {
                window.location.href = paymentIntent?.url;
              }
            }).catch((err) => {
              setError('Error creating Ideal payment');
            })
            .finally(() => {
              setIsPaymentLoading(false);
            });

          return;
        } else if (data.payment_method === 'sepa') {
          createPaymentIntent('sepa_debit', user)
            .then((paymentIntent) => {
              sepaPayment(paymentIntent.clientSecret)
                .then(() => {
                  window.location.href = `${window.location.origin}/${language}/paymentsuccess?orderId=${orderData.id}&paymentMethod=sepa`;
                })
                .catch(() => {
                  setError('Error creating SEPA payment');
                })
                .finally(() => {
                  setIsPaymentLoading(false);
                });
            })
            .catch(() => {
              setIsPaymentLoading(false);
              setError('Invalid IBAN for SEPA transactions. Ensure the IBAN is correctly formatted.');
            });
          return;
        }

        const payment_url = generatePaymentUrl({
          order: orderData,
          paymentInfo,
          paymentMethod: data.payment_method as i.PaymentMethods,
          language,
        });

        // JSON Template for GTAG
        // Google default payload, unused fields disabled
        const eventPayload = {
          currency: activeItem?.price.currency,
          value: activeItem?.price.amount,
          // coupon: 'coupon',
          items: [
            {
              item_id: activeItem?.id,
              item_name: activeItem?.name,
              // affiliation: 'affiliation',
              // coupon: 'coupon',
              // discount: 'discount',
              index: 0,
              // item_brand: 'item_brand',
              item_category: activeItem?.seriesType,
              // item_list_id: 'item_list_id',
              // item_list_name: 'item_list_name',
              // item_variant: 'item_variant',
              // location_id: 'location_id',
              price: activeItem?.price.currency,
              quantity: 1,
            },
          ],
        };
        triggerGtagEvent('begin_checkout', eventPayload);

        if (payment_url && isBrowser()) {
          window.location.href = payment_url;
        }
      })
      .catch((err) => console.error(err));
  };
  return (
    <StyledForm onSubmit={handleSubmit(handleCheckout)}>
      <FormContainer>
        {activeItem?.unlimited && (
          <>
            <Input
              label={t('address')}
              defaultValue={defaultValues?.address}
              error={errors.address}
              {...register('address',
                { ...validation.required })}
            />
            <AddressContainer>
              <Input
                label={t('zipcode')}
                defaultValue={defaultValues?.zip}
                error={errors.zip}
                {...register('zip',
                  { ...validation.required })}
              />
              <Input
                label={t('city')}
                defaultValue={defaultValues?.city}
                error={errors.city}
                {...register('city',
                  { ...validation.required })}
              />
            </AddressContainer>
            <Input
              label={t('phone')}
              defaultValue={defaultValues?.phone}
              {...register('phone',
                { ...validation.phone, ...validation.required },
              )}
              error={errors?.phone}
            />
            {watch('payment_method') === 'sepa' && (
              <Input
                label={t('iban')}
                {...register('iban',
                  { ...validation.iban, ...validation.required },
                )}
                error={errors?.iban}
              />
            )}
          </>
        )}
        <PaymentContainer>
          <ExtendedText>{t('payment method')}</ExtendedText>
          <Controller
            name="payment_method"
            control={control}
            render={(({ field: { onChange, value } }) => (
              <InputRadio
                {...{ value, onChange }}
                direction="horizontal"
                items={activeItem?.unlimited ? memberShipOptions : classOptions}
              />
            ))}
          />
          {activeItem?.unlimited && (
            <>
              <Text>{t('sepa explanation')}</Text>
              <Text $margin="0 0 20px">{smallDescription}</Text>
            </>
          )}
        </PaymentContainer>
      </FormContainer>
      <CheckoutFooter>
        <StepButtonGroup {...{ currentStep }} />
        <Button
          type="submit"
          size="fullWidth"
          disabled={!isValid}
          $isValid={isValid}
          isLoading={isLoading || isPaymentLoading}
        >
          {t('buy now')}
        </Button>
        {error && (
          <Text variant="red" $margin="12px 0">
            {error}
          </Text>
        )}
      </CheckoutFooter>
    </StyledForm>
  );
};
type MembershipForm = {
  address: string;
  zip: string;
  city: string;
  phone: string;
  payment_method: string;
};
